import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "./Dashboard";
import { toast } from "sonner";
import i18n from "i18next";
import {
    FEEDBACK_TYPES, FEEDBACK_STATUS, FEEDBACK_PRIORITY, fmtDate,
} from "@/lib/feedback";
import FeedbackModal from "@/components/feedback/FeedbackModal";
import FeedbackDashboard from "./FeedbackDashboard";
import {
    Filter, Plus, Sparkles, Send, Lock, X, MessageSquare, RefreshCw,
    Wand2, ArrowRight, ImageIcon, AlertTriangle, Loader2, ExternalLink,
    BarChart3,
} from "lucide-react";

const STATUS_KEYS = ["new", "in_progress", "retesting", "resolved"];

export default function Feedback() {
    const { user } = useAuth();
    const isAdmin = user?.role === "owner" || user?.role === "admin";
    const [tab, setTab] = useState(isAdmin ? "dashboard" : "mine");
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [openNew, setOpenNew] = useState(false);
    const [filter, setFilter] = useState({ status: "", type: "", priority: "", stalled: false, reporter: null });
    const EMPTY_FILTER = { status: "", type: "", priority: "", stalled: false, reporter: null };

    // Dashboard drill-down: klik op een KPI/verdeling → gefilterde lijst.
    const drill = (patch) => {
        setFilter({ ...EMPTY_FILTER, ...patch });
        setTab("all");
    };

    const refresh = async () => {
        if (tab === "dashboard") { setLoading(false); return; }
        setLoading(true);
        const url = tab === "all" ? "/feedback" : "/feedback/mine";
        const params = tab === "all" ? {
            ...(filter.status ? { status: filter.status } : {}),
            ...(filter.type ? { type: filter.type } : {}),
            ...(filter.priority ? { priority: filter.priority } : {}),
            ...(filter.stalled ? { stalled: true } : {}),
            ...(filter.reporter?.id ? { reporter_id: filter.reporter.id } : {}),
        } : {};
        try {
            const [r1, r2] = await Promise.allSettled([
                api.get(url, { params }),
                isAdmin ? api.get("/feedback/stats") : Promise.resolve({ data: null }),
            ]);
            if (r1.status === "fulfilled") setItems(r1.value.data);
            else { setItems([]); toast.error(i18n.t("feedback.load_failed")); }
            setStats(r2.status === "fulfilled" ? r2.value.data : null);
        } finally { setLoading(false); }
    };
    useEffect(() => { refresh(); }, [tab, filter.status, filter.type, filter.priority, filter.stalled, filter.reporter?.id]);

    return (
        <PageHeader
            title="Feedback"
            subtitle="Bugs, ideeën en vragen — wij lezen alles."
            actions={
                <div className="flex items-center gap-2">
                    <button onClick={refresh} aria-label="Refresh"
                        data-testid="feedback-refresh"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-mono uppercase tracking-[0.16em] text-[var(--jr-text-2)] hover:text-white border border-white/10 hover:bg-white/5">
                        <RefreshCw size={12} />
                    </button>
                    <button onClick={() => setOpenNew(true)}
                        data-testid="feedback-page-new"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--jr-brand)] text-white text-sm font-medium hover:bg-[var(--jr-brand-2)]">
                        <Plus size={14} /> Nieuwe feedback
                    </button>
                </div>
            }
        >
            {/* Tabs */}
            {isAdmin && (
                <div className="mb-6 flex items-center gap-1 border-b border-white/8" data-testid="feedback-tabs">
                    <TabBtn active={tab === "dashboard"} onClick={() => setTab("dashboard")} testid="tab-dashboard">
                        <BarChart3 size={12} className="inline mr-1 -mt-0.5" /> Dashboard
                    </TabBtn>
                    <TabBtn active={tab === "all"} onClick={() => setTab("all")} testid="tab-all">
                        Alle feedback (admin)
                    </TabBtn>
                    <TabBtn active={tab === "mine"} onClick={() => setTab("mine")} testid="tab-mine">
                        Mijn feedback
                    </TabBtn>
                </div>
            )}

            {tab === "dashboard" && isAdmin ? (
                <FeedbackDashboard onDrill={drill} />
            ) : (
                <>
                {/* Stats (admin only, all tab) */}
                {isAdmin && tab === "all" && stats && (
                    <div className="mb-6 grid grid-cols-2 md:grid-cols-8 gap-3" data-testid="feedback-stats">
                        <StatCard label="Totaal" value={stats.total} accent="white" />
                        {STATUS_KEYS.map((s) => (
                            <StatCard key={s} label={FEEDBACK_STATUS[s].label}
                                value={stats.by_status[s] || 0}
                                color={FEEDBACK_STATUS[s].color}
                                onClick={() => setFilter((f) => ({ ...f, status: f.status === s ? "" : s }))}
                                active={filter.status === s}
                            />
                        ))}
                        {Object.keys(FEEDBACK_TYPES).map((tp) => (
                            <StatCard key={tp} label={FEEDBACK_TYPES[tp].label}
                                value={stats.by_type[tp] || 0}
                                color={FEEDBACK_TYPES[tp].color}
                                onClick={() => setFilter((f) => ({ ...f, type: f.type === tp ? "" : tp }))}
                                active={filter.type === tp}
                            />
                        ))}
                    </div>
                )}

                {(filter.status || filter.type || filter.priority || filter.stalled || filter.reporter) && (
                    <div className="mb-4 flex items-center gap-2 text-xs flex-wrap" data-testid="feedback-active-filters">
                        <Filter size={12} className="text-[var(--jr-text-2)]" />
                        <span className="text-[var(--jr-text-2)]">{i18n.t("feedback.filters_active")}:</span>
                        {filter.status && <FilterChip>{FEEDBACK_STATUS[filter.status]?.label || filter.status}</FilterChip>}
                        {filter.type && <FilterChip>{FEEDBACK_TYPES[filter.type]?.label || filter.type}</FilterChip>}
                        {filter.priority && <FilterChip>{filter.priority.toUpperCase()}</FilterChip>}
                        {filter.stalled && <FilterChip>{i18n.t("fbd.stalled")}</FilterChip>}
                        {filter.reporter && <FilterChip>{filter.reporter.name}</FilterChip>}
                        <button onClick={() => setFilter({ ...EMPTY_FILTER })}
                            data-testid="feedback-clear-filter"
                            className="underline text-[var(--jr-brand)]">{i18n.t("feedback.clear")}</button>
                    </div>
                )}

                {/* List */}
                {loading ? (
                    <div className="text-[var(--jr-text-2)]">{i18n.t("common.loading")}</div>
                ) : items.length === 0 ? (
                    <EmptyState onNew={() => setOpenNew(true)} mine={tab === "mine"} />
                ) : (
                    <div className="space-y-2" data-testid="feedback-list">
                        {items.map((it) => (
                            <FeedbackRow key={it.id} item={it} isAdmin={isAdmin && tab === "all"}
                                onOpen={() => setSelected(it)} />
                        ))}
                    </div>
                )}
                </>
            )}

            <FeedbackModal open={openNew} onClose={() => setOpenNew(false)} onCreated={refresh} />
            {selected && (
                <FeedbackDetail item={selected} isAdmin={isAdmin && tab === "all"}
                    onClose={() => setSelected(null)}
                    onChanged={() => { refresh(); }} />
            )}
        </PageHeader>
    );
}

/* ----- row card ----- */
function FeedbackRow({ item, isAdmin, onOpen }) {
    const t = FEEDBACK_TYPES[item.type] || FEEDBACK_TYPES.bug;
    const s = FEEDBACK_STATUS[item.status] || FEEDBACK_STATUS.new;
    const Icon = t.icon; const SIcon = s.icon;
    return (
        <button onClick={onOpen} type="button" data-testid={`feedback-row-${item.id}`}
            className="w-full text-left rounded-xl border border-white/8 bg-[var(--jr-surface)] p-4 hover:border-white/20 hover:bg-white/[0.02] transition-colors">
            <div className="flex items-start gap-4">
                <div className="w-9 h-9 rounded-lg grid place-items-center shrink-0"
                    style={{ background: t.color + "1F", color: t.color }}>
                    <Icon size={16} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <h4 className="text-white font-medium truncate">{item.title}</h4>
                        {item.attachment_id && <ImageIcon size={12} className="text-[var(--jr-text-3)] shrink-0" />}
                    </div>
                    <p className="text-xs text-[var(--jr-text-2)] line-clamp-1 mt-0.5">{item.message}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--jr-text-3)]">
                        <span>{fmtDate(item.created_at)}</span>
                        {isAdmin && <><span>·</span><span>{item.user_name}</span></>}
                        {item.replies?.length > 0 && (
                            <><span>·</span><span className="inline-flex items-center gap-1"><MessageSquare size={10} /> {item.replies.length}</span></>
                        )}
                        {item.ai_triage && (
                            <><span>·</span><span className="inline-flex items-center gap-1 text-[var(--jr-info)]"><Sparkles size={10} /> AI</span></>
                        )}
                    </div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                    <Badge color={s.color} icon={SIcon}>{s.label}</Badge>
                    <PrioBadge p={item.priority} />
                </div>
            </div>
        </button>
    );
}

/* ----- detail modal ----- */
function FeedbackDetail({ item, isAdmin, onClose, onChanged }) {
    const [cur, setCur] = useState(item);
    const [reply, setReply] = useState("");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [triaging, setTriaging] = useState(false);
    const [attachment, setAttachment] = useState(null);

    useEffect(() => {
        if (cur.attachment_id) {
            api.get(`/feedback/attachments/${cur.attachment_id}`)
                .then((r) => setAttachment(r.data))
                .catch(() => setAttachment(null));
        }
    }, [cur.attachment_id]);

    const refreshOne = async () => {
        const { data } = await api.get(`/feedback/${cur.id}`);
        setCur(data); onChanged?.();
    };

    const patch = async (changes) => {
        setBusy(true);
        try {
            const { data } = await api.patch(`/feedback/${cur.id}`, changes);
            setCur(data); onChanged?.();
            toast.success(i18n.t("common.updated") + ".");
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setBusy(false); }
    };

    const sendReply = async (e) => {
        e?.preventDefault();
        if (!reply.trim()) return;
        setBusy(true);
        try {
            await api.post(`/feedback/${cur.id}/reply`, { message: reply.trim() });
            setReply(""); await refreshOne();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setBusy(false); }
    };

    const sendNote = async (e) => {
        e?.preventDefault();
        if (!note.trim()) return;
        setBusy(true);
        try {
            await api.post(`/feedback/${cur.id}/notes`, { message: note.trim() });
            setNote(""); await refreshOne();
            toast.success(i18n.t("feedback.note_added"));
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setBusy(false); }
    };

    const runTriage = async () => {
        setTriaging(true);
        try {
            await api.post(`/feedback/${cur.id}/triage`);
            await refreshOne();
            toast.success(i18n.t("feedback.triage_done"));
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("feedback.triage_failed")); }
        finally { setTriaging(false); }
    };
    const applyTriage = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/feedback/${cur.id}/triage/apply`);
            setCur(data); onChanged?.();
            toast.success(i18n.t("feedback.suggestion_applied"));
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setBusy(false); }
    };

    const T = FEEDBACK_TYPES[cur.type] || FEEDBACK_TYPES.bug;
    const S = FEEDBACK_STATUS[cur.status] || FEEDBACK_STATUS.new;
    const Icon = T.icon;

    return (
        <div className="fixed inset-0 z-[60] grid place-items-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={(e) => e.target === e.currentTarget && onClose?.()}>
            <div className="w-full max-w-5xl max-h-[90vh] bg-[var(--jr-surface)] border border-white/10 rounded-2xl flex flex-col overflow-hidden jr-fadein"
                data-testid="feedback-detail">
                {/* Header */}
                <div className="p-6 border-b border-white/8 flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg grid place-items-center shrink-0"
                        style={{ background: T.color + "1F", color: T.color }}>
                        <Icon size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--jr-text-3)] flex-wrap">
                            <span>{T.label}</span>
                            <span>·</span>
                            <span>{fmtDate(cur.created_at)}</span>
                            <span>·</span>
                            <span>{cur.user_name}</span>
                        </div>
                        <h2 className="font-display text-2xl tracking-tight mt-1">{cur.title}</h2>
                        {cur.page_url && (
                            <a href={cur.page_url} target="_blank" rel="noreferrer"
                                data-testid="feedback-source-url"
                                className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-mono px-2.5 py-1 rounded-full border border-[var(--jr-info)]/30 bg-[var(--jr-info)]/10 text-[var(--jr-info)] hover:bg-[var(--jr-info)]/20 max-w-full">
                                <ExternalLink size={11} className="shrink-0" />
                                <span className="truncate">{cur.page_url}</span>
                            </a>
                        )}
                    </div>
                    <button onClick={onClose} aria-label="Sluit" data-testid="feedback-detail-close"
                        className="text-[var(--jr-text-2)] hover:text-white"><X size={20} /></button>
                </div>

                {/* Body: grid with main + admin sidebar */}
                <div className={`flex-1 overflow-y-auto grid ${isAdmin ? "md:grid-cols-[1fr_300px]" : "grid-cols-1"}`}>
                    {/* Main thread */}
                    <div className="p-6 space-y-5 border-r border-white/8">
                        {/* Original message */}
                        <div className="rounded-xl border border-white/10 bg-[var(--jr-surface-2)] p-4">
                            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] mb-1.5">{cur.user_name} schreef</div>
                            <p className="text-sm text-white whitespace-pre-wrap leading-relaxed">{cur.message}</p>
                            {attachment && (
                                <a href={attachment.data_url} target="_blank" rel="noreferrer"
                                    data-testid="feedback-screenshot"
                                    className="mt-3 block rounded-lg overflow-hidden border border-white/10 hover:opacity-90">
                                    <img src={attachment.data_url} alt="Screenshot bijlage"
                                        className="max-h-[280px] w-full object-cover" />
                                </a>
                            )}
                        </div>

                        {/* AI triage block */}
                        {cur.ai_triage && (
                            <div className="rounded-xl border border-[var(--jr-info)]/30 bg-[var(--jr-info)]/5 p-4" data-testid="ai-triage-block">
                                <div className="flex items-center gap-2 mb-2">
                                    <Sparkles size={14} className="text-[var(--jr-info)]" />
                                    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-info)]">AI Triage</div>
                                    <span className="text-[10px] text-[var(--jr-text-3)] ml-auto">{fmtDate(cur.ai_triage.triaged_at)}</span>
                                </div>
                                <p className="text-sm text-white">{cur.ai_triage.summary}</p>
                                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                                    {cur.ai_triage.suggested_type && (
                                        <span className="px-2 py-1 rounded-full bg-white/5 border border-white/10">Type → <b>{FEEDBACK_TYPES[cur.ai_triage.suggested_type]?.label}</b></span>
                                    )}
                                    {cur.ai_triage.suggested_priority && (
                                        <span className="px-2 py-1 rounded-full bg-white/5 border border-white/10">Prio → <b>{FEEDBACK_PRIORITY[cur.ai_triage.suggested_priority]?.label}</b></span>
                                    )}
                                    {cur.ai_triage.is_likely_duplicate && (
                                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[var(--jr-amber)]/15 text-[var(--jr-amber)]"><AlertTriangle size={10} /> Mogelijk duplicaat</span>
                                    )}
                                    {isAdmin && (
                                        <button onClick={applyTriage} disabled={busy}
                                            data-testid="ai-triage-apply"
                                            className="ml-auto inline-flex items-center gap-1 text-[var(--jr-brand)] hover:text-[var(--jr-brand-2)] text-xs">
                                            Pas toe <ArrowRight size={12} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Replies */}
                        <div>
                            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] mb-2">
                                {i18n.t("feedback.conversation")} ({cur.replies?.length || 0})
                            </div>
                            {(cur.replies || []).length === 0 ? (
                                <div className="text-xs text-[var(--jr-text-3)] italic">{i18n.t("feedback.no_replies")} {isAdmin ? i18n.t("feedback.reply_hint_admin") : i18n.t("feedback.reply_hint_user")}</div>
                            ) : (
                                <ul className="space-y-3">
                                    {(cur.replies || []).map((r) => (
                                        <li key={r.id} className={`flex ${r.author_role === "admin" ? "justify-start" : "justify-end"}`}
                                            data-testid={`reply-${r.id}`}>
                                            <div className={`max-w-[80%] rounded-xl p-3 text-sm ${
                                                r.author_role === "admin"
                                                    ? "bg-[var(--jr-brand)]/10 border border-[var(--jr-brand)]/30"
                                                    : "bg-white/[0.04] border border-white/10"
                                            }`}>
                                                <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] mb-1">{r.author_name} · {fmtDate(r.created_at)}</div>
                                                <div className="text-white whitespace-pre-wrap leading-relaxed">{r.message}</div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {/* Reply composer */}
                        <form onSubmit={sendReply} className="rounded-xl border border-white/10 bg-[var(--jr-surface-2)] p-3">
                            <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={2}
                                placeholder={isAdmin ? i18n.t("feedback.reply_ph_admin") : i18n.t("feedback.reply_ph_user")}
                                data-testid="feedback-reply-input"
                                className="w-full bg-transparent border-0 outline-none text-sm text-white resize-none" />
                            <div className="flex justify-end mt-1">
                                <button type="submit" disabled={busy || !reply.trim()}
                                    data-testid="feedback-reply-submit"
                                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--jr-brand)] text-white text-xs font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-50">
                                    <Send size={12} /> Verstuur
                                </button>
                            </div>
                        </form>
                    </div>

                    {/* Admin sidebar */}
                    {isAdmin && (
                        <aside className="p-6 bg-[var(--jr-surface-2)]/40 space-y-5">
                            <Field label="Status">
                                <div className="flex flex-wrap gap-1">
                                    {STATUS_KEYS.map((s) => (
                                        <button key={s} onClick={() => patch({ status: s })} disabled={busy}
                                            data-testid={`admin-status-${s}`}
                                            className={`px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-[0.16em] ${
                                                cur.status === s
                                                    ? "text-black"
                                                    : "border border-white/10 text-[var(--jr-text-2)] hover:bg-white/5"
                                            }`}
                                            style={cur.status === s ? { background: FEEDBACK_STATUS[s].color } : {}}>
                                            {FEEDBACK_STATUS[s].label}
                                        </button>
                                    ))}
                                </div>
                            </Field>
                            <Field label="Prioriteit">
                                <div className="flex flex-wrap gap-1">
                                    {["p0", "p1", "p2"].map((p) => (
                                        <button key={p} onClick={() => patch({ priority: p })} disabled={busy}
                                            data-testid={`admin-prio-${p}`}
                                            className={`px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-[0.16em] ${
                                                cur.priority === p
                                                    ? "text-black"
                                                    : "border border-white/10 text-[var(--jr-text-2)] hover:bg-white/5"
                                            }`}
                                            style={cur.priority === p ? { background: FEEDBACK_PRIORITY[p].color } : {}}>
                                            {p.toUpperCase()}
                                        </button>
                                    ))}
                                </div>
                            </Field>
                            <Field label="Type">
                                <select value={cur.type} disabled={busy}
                                    onChange={(e) => patch({ type: e.target.value })}
                                    data-testid="admin-type"
                                    className="w-full bg-[var(--jr-surface)] border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white">
                                    {Object.keys(FEEDBACK_TYPES).map((k) => (
                                        <option key={k} value={k}>{FEEDBACK_TYPES[k].label}</option>
                                    ))}
                                </select>
                            </Field>
                            <Field label="Tags (comma-separated)">
                                <input defaultValue={(cur.tags || []).join(", ")}
                                    onBlur={(e) => {
                                        const tags = e.target.value.split(",").map((s) => s.trim()).filter(Boolean);
                                        const same = JSON.stringify(tags) === JSON.stringify(cur.tags || []);
                                        if (!same) patch({ tags });
                                    }}
                                    data-testid="admin-tags"
                                    placeholder="mobile, onboarding…"
                                    className="w-full bg-[var(--jr-surface)] border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white" />
                            </Field>

                            <div>
                                <button onClick={runTriage} disabled={triaging || busy}
                                    data-testid="admin-run-triage"
                                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full border border-[var(--jr-info)]/30 bg-[var(--jr-info)]/10 text-[var(--jr-info)] text-xs font-medium hover:bg-[var(--jr-info)]/15 disabled:opacity-50">
                                    {triaging ? <><Loader2 className="animate-spin" size={12} /> {i18n.t("feedback.ai_busy")}</> : <><Wand2 size={12} /> Run AI-triage</>}
                                </button>
                                <div className="text-[10px] text-[var(--jr-text-3)] mt-1 text-center">~1 credit · categoriseert + prio + duplicate check</div>
                            </div>

                            {/* Internal notes */}
                            <div>
                                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] mb-2 flex items-center gap-1">
                                    <Lock size={10} /> Interne notes
                                </div>
                                <ul className="space-y-2 mb-2">
                                    {(cur.internal_notes || []).map((n) => (
                                        <li key={n.id} className="rounded-lg bg-[var(--jr-bg)] border border-white/8 p-2 text-xs"
                                            data-testid={`note-${n.id}`}>
                                            <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)]">{n.author_name} · {fmtDate(n.created_at)}</div>
                                            <div className="text-white whitespace-pre-wrap mt-0.5">{n.message}</div>
                                        </li>
                                    ))}
                                </ul>
                                <form onSubmit={sendNote}>
                                    <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                                        placeholder={i18n.t("feedback.note_ph")}
                                        data-testid="admin-note-input"
                                        className="w-full bg-[var(--jr-surface)] border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white resize-none" />
                                    <button type="submit" disabled={busy || !note.trim()}
                                        data-testid="admin-note-submit"
                                        className="mt-1 w-full px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-2)] hover:bg-white/10 disabled:opacity-50">
                                        Note toevoegen
                                    </button>
                                </form>
                            </div>
                        </aside>
                    )}
                </div>
            </div>
        </div>
    );
}

/* ----- tiny UI helpers ----- */
function FilterChip({ children }) {
    return (
        <span className="px-2 py-0.5 rounded-full bg-white/6 border border-white/10 text-white text-[11px]">
            {children}
        </span>
    );
}

function StatCard({ label, value, color, accent, onClick, active }) {
    return (
        <button type="button" onClick={onClick} disabled={!onClick}
            className={`text-left rounded-lg border p-3 transition-colors ${
                active ? "border-white/30 bg-white/[0.05]" : "border-white/8 bg-[var(--jr-surface)]"
            } ${onClick ? "hover:border-white/20 hover:bg-white/[0.03]" : "cursor-default"}`}>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{label}</div>
            <div className="font-display text-2xl mt-0.5" style={{ color: color || "white" }}>{value}</div>
        </button>
    );
}

function Badge({ color, icon: Icon, children }) {
    return (
        <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.16em] px-2 py-1 rounded-full"
            style={{ background: color + "1F", color }}>
            {Icon && <Icon size={10} />} {children}
        </span>
    );
}

function PrioBadge({ p }) {
    const o = FEEDBACK_PRIORITY[p] || FEEDBACK_PRIORITY.p2;
    return <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-2)]" style={{ color: o.color }}>{p.toUpperCase()}</span>;
}

function TabBtn({ active, onClick, children, testid }) {
    return (
        <button type="button" onClick={onClick} data-testid={testid}
            className={`px-4 py-2.5 text-sm border-b-2 transition-colors ${
                active
                    ? "border-[var(--jr-brand)] text-white"
                    : "border-transparent text-[var(--jr-text-2)] hover:text-white"
            }`}>{children}</button>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] mb-1.5">{label}</div>
            {children}
        </div>
    );
}

function EmptyState({ onNew, mine }) {
    return (
        <div className="text-center py-16 rounded-xl border border-dashed border-white/10 bg-[var(--jr-surface)]"
            data-testid="feedback-empty">
            <div className="font-display text-2xl tracking-tight">{mine ? i18n.t("feedback.empty_mine") : i18n.t("feedback.empty_admin")}</div>
            <p className="text-sm text-[var(--jr-text-2)] mt-2 max-w-md mx-auto">
                {mine
                    ? i18n.t("feedback.empty_mine_sub")
                    : i18n.t("feedback.empty_admin_sub")}
            </p>
            <button onClick={onNew} data-testid="feedback-empty-cta"
                className="mt-4 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[var(--jr-brand)] text-white text-sm font-medium hover:bg-[var(--jr-brand-2)]">
                <Plus size={14} /> Eerste melding
            </button>
        </div>
    );
}
